import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  effect,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { GlobalStore } from '@maximizer/core/shared/data-access';
import { UserRights } from '@maximizer/core/shared/domain';
import { UI_BUTTONS } from '@maximizer/core/shared/ui/components/buttons';
import { UI_LOADER } from '@maximizer/core/shared/ui/components/loader';
import { UI_KENDO } from '@maximizer/core/shared/ui/kendo';
import { CaseService } from '@maximizer/outlook/case/data-access';
import { CaseCardComponent } from '@maximizer/outlook/case/ui';
import { EmailDetailComponent } from '@maximizer/outlook/interaction-log/ui';
import {
  RelatedService,
  haveSameEntryKeys,
} from '@maximizer/outlook/main/data-access';
import { OpportunityService } from '@maximizer/outlook/opportunity/data-access';
import { OpportunityCardComponent } from '@maximizer/outlook/opportunity/ui';
import {
  OutlookService,
  OutlookStore,
  VerticalService,
} from '@maximizer/outlook/shared/data-access';
import {
  CaseCard,
  EntryType,
  OpportunityCard,
  OutlookEmailDetail,
  MIN_SEARCH_LENGTH,
} from '@maximizer/outlook/shared/domain';
import {
  OUTLOOK_SHARED_UI,
  OutlookNotificationComponent,
} from '@maximizer/outlook/shared/ui';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_TEXTBOX } from '@progress/kendo-angular-inputs';
import { catchError, finalize, of, Subject, tap } from 'rxjs';

@Component({
  selector: 'maximizer-outlook-related-detail',
  templateUrl: './related-detail.component.html',
  styleUrls: ['./related-detail.component.scss'],
  imports: [
    CaseCardComponent,
    EmailDetailComponent,
    KENDO_BUTTON,
    KENDO_TEXTBOX,
    OpportunityCardComponent,
    OUTLOOK_SHARED_UI,
    TranslatePipe,
    UI_BUTTONS,
    UI_KENDO,
    UI_LOADER,
  ],
})
export class OutlookRelatedDetailComponent implements OnInit, OnDestroy {
  @ViewChild('notification')
  notification!: OutlookNotificationComponent;
  @ViewChild('scrollContainer')
  scrollContainer!: ElementRef;

  private readonly searchSubject = new Subject<string>();
  private readonly destroy$ = new Subject<void>();
  private readonly scrollThreshold = 100;

  EntryType = EntryType;
  currentAbEntryIds: string[] = [];
  entryType?: EntryType;
  opportunities: OpportunityCard[] = [];
  displayedOpportunities: OpportunityCard[] = [];
  cases: CaseCard[] = [];
  displayedCases: CaseCard[] = [];
  userRights?: UserRights;
  saveEmailDetail = false;
  saveEmailDetailProperties?: OutlookEmailDetail;
  searchPlaceholder = '';
  pageSize = 10;
  currentPage = 1;
  searchInput = '';
  searchPhrase?: string;
  showMoreVisible = false;
  scrollToTopVisible = false;
  trackingEntryType: EntryType = EntryType.Opportunity;

  isLoading = {
    spinner: false,
    skeleton: false,
  };

  constructor(
    private readonly opportunityService: OpportunityService,
    private readonly caseService: CaseService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly translate: TranslateService,
    private readonly outlookService: OutlookService,
    private readonly relatedService: RelatedService,
    private readonly verticalService: VerticalService,
    public outlookStore: OutlookStore,
    public globalStore: GlobalStore,
  ) {
    effect(() =>
      this.reloadOnEntryKeysChange(this.relatedService.relatedEntryKeys()),
    );
  }

  private reloadOnEntryKeysChange(entryKeys: string[]): void {
    const isSearching = !!this.searchPhrase;
    if (isSearching || haveSameEntryKeys(entryKeys, this.currentAbEntryIds)) {
      return;
    }

    this.currentAbEntryIds = entryKeys;
    this.loadEntries();
  }

  private loadEntries(): void {
    this.resetPagination();

    if (!this.currentAbEntryIds.length) {
      this.isLoading.skeleton = false;
      this.opportunities = [];
      this.cases = [];
      return;
    }

    this.isLoading.skeleton = true;

    if (this.entryType === EntryType.Opportunity) {
      this.loadOpportunities();
    } else if (this.entryType === EntryType.Case) {
      this.loadCases();
    } else {
      this.isLoading.skeleton = false;
    }
  }

  ngOnInit(): void {
    this.activatedRoute.params.subscribe((params) => {
      this.currentAbEntryIds = this.relatedService.relatedEntryKeys();
      this.entryType = params['entry'];

      this.isLoading.skeleton = true;
      this.resetPagination();
      this.setUserRights();
      this.setupSearchSubject();
      this.updateSearchPlaceholder();

      const existingSearchContext = this.outlookStore
        .relatedSearchPhrase()
        ?.trim();

      if (
        existingSearchContext &&
        existingSearchContext.length >= MIN_SEARCH_LENGTH
      ) {
        this.searchInput = existingSearchContext;
        this.performSearch(existingSearchContext);
      } else {
        if (existingSearchContext) {
          this.outlookStore.clearRelatedSearchContext();
        }
        this.loadEntries();
      }
    });
  }

  private updateSearchPlaceholder(): void {
    const label =
      this.entryType === EntryType.Case
        ? this.verticalService.translateVertical('cases')
        : this.translate.instant('outlook.entries.opportunities');

    this.searchPlaceholder = this.translate.instant(
      'outlook.main.related.searchPlaceholderSingle',
      { label },
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSearchSubject(): void {
    this.relatedService.setupSearchSubject(
      this.searchSubject,
      this.destroy$,
      (searchTerm) => this.performSearch(searchTerm),
      MIN_SEARCH_LENGTH,
    );
  }

  private performSearch(searchTerm: string): void {
    this.searchPhrase = searchTerm;
    this.isLoading.skeleton = true;

    if (this.entryType === EntryType.Opportunity) {
      this.relatedService
        .searchOpportunities(searchTerm)
        .pipe(
          finalize(() => {
            this.isLoading.skeleton = false;
            this.getSavedEmails(EntryType.Opportunity);
          }),
        )
        .subscribe((results) => {
          this.opportunities = results;
          this.resetPagination();
          this.updateDisplayedEntries();
        });
    } else if (this.entryType === EntryType.Case) {
      this.relatedService
        .searchCases(searchTerm)
        .pipe(
          finalize(() => {
            this.isLoading.skeleton = false;
            this.getSavedEmails(EntryType.Case);
          }),
        )
        .subscribe((results) => {
          this.cases = results;
          this.resetPagination();
          this.updateDisplayedEntries();
        });
    }
  }

  private clearSearch(): void {
    this.outlookStore.clearRelatedSearchContext();
    this.searchPhrase = undefined;
    this.currentAbEntryIds = this.relatedService.relatedEntryKeys();

    this.loadEntries();
  }

  onSearchInput(value: string): void {
    this.searchInput = value;
    if (value.length === 0) {
      // Push the empty value so debounceTime supersedes any pending term.
      this.searchSubject.next(value);
      this.clearSearch();
      this.outlookStore.clearRelatedSearchContext();
    } else {
      this.searchSubject.next(value);

      const trimmedValue = value.trim();
      if (trimmedValue.length > 0) {
        this.outlookStore.setRelatedSearchContext(trimmedValue);
      } else {
        this.outlookStore.clearRelatedSearchContext();
      }
    }
  }

  resetPagination(): void {
    this.currentPage = 1;
    this.displayedOpportunities = [];
    this.displayedCases = [];
    this.showMoreVisible = false;
  }

  setUserRights(): void {
    if (!this.globalStore.session()) return;
    this.userRights = this.globalStore.session()?.user.rights;
  }

  loadCases(): void {
    const requestKeys = this.currentAbEntryIds;

    this.caseService
      .getCaseByContactId(requestKeys)
      .pipe(
        tap((result: CaseCard[]) => {
          // A newer load replaced currentAbEntryIds; this response is stale.
          if (requestKeys !== this.currentAbEntryIds) return;
          this.cases = result;
          this.updateDisplayedEntries();
        }),
        catchError((error) => {
          console.error('Error fetching cases:', error);
          return of([]);
        }),
        finalize(() => {
          if (requestKeys !== this.currentAbEntryIds) return;
          this.isLoading.skeleton = false;
          this.getSavedEmails(EntryType.Case);
        }),
      )
      .subscribe();
  }

  loadOpportunities(): void {
    const requestKeys = this.currentAbEntryIds;

    this.opportunityService
      .getOpportunityByContactId(requestKeys)
      .pipe(
        tap((result: OpportunityCard[]) => {
          if (requestKeys !== this.currentAbEntryIds) return;
          this.opportunities = result;
          this.updateDisplayedEntries();
        }),
        catchError((error) => {
          console.error('Error fetching opportunities:', error);
          return of([]);
        }),
        finalize(() => {
          if (requestKeys !== this.currentAbEntryIds) return;
          this.isLoading.skeleton = false;
          this.getSavedEmails(EntryType.Opportunity);
        }),
      )
      .subscribe();
  }

  get cardMode(): 'read' | 'compose' {
    return this.relatedService.isComposeMode ? 'compose' : 'read';
  }

  async getSavedEmails(type: EntryType): Promise<void> {
    try {
      const allEmails = await this.relatedService.getSavedEmails();

      if (type === EntryType.Opportunity) {
        this.relatedService.markSavedOpportunities(
          this.opportunities,
          allEmails.opportunities,
        );
      } else if (type === EntryType.Case) {
        this.relatedService.markSavedCases(this.cases, allEmails.cases);
      }
    } catch (error) {
      console.error('Error retrieving saved emails:', error);
    }
  }

  onShowMore(): void {
    this.currentPage++;
    this.updateDisplayedEntries();
  }

  updateDisplayedEntries(): void {
    const startIndex = 0;
    const endIndex = this.currentPage * this.pageSize;

    if (this.entryType === EntryType.Opportunity) {
      this.displayedOpportunities = this.opportunities.slice(
        startIndex,
        endIndex,
      );
      this.showMoreVisible = endIndex < this.opportunities.length;
    } else if (this.entryType === EntryType.Case) {
      this.displayedCases = this.cases.slice(startIndex, endIndex);
      this.showMoreVisible = endIndex < this.cases.length;
    }
  }

  @HostListener('scroll', ['$event'])
  onScroll(event: Event): void {
    const target = event.target as HTMLElement;
    const scrollTop = target.scrollTop;
    this.scrollToTopVisible = scrollTop > this.scrollThreshold;
  }

  scrollToTop(): void {
    if (this.scrollContainer?.nativeElement) {
      this.scrollContainer.nativeElement.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }

  async saveEmail(key: string, type: EntryType): Promise<void> {
    this.trackingEntryType = type;

    if (this.outlookStore.preferences().saveEmailDetail) {
      await this.openSaveEmailDetails(key, type);
    } else {
      await this.performSaveEmail(key, type);
    }
  }

  async openSaveEmailDetails(key: string, type: EntryType): Promise<void> {
    // Properties must be resolved before the dialog renders: EmailDetailComponent
    // reads its emailProperties input once when its field options arrive.
    this.saveEmailDetailProperties = {
      subject: await this.outlookService.getSubject(),
      type,
      key,
    };
    this.saveEmailDetail = true;
  }

  private async performSaveEmail(key: string, type: EntryType) {
    this.isLoading.spinner = true;

    try {
      const result = await this.relatedService.performSaveEmail(key, type);

      if (!result.success) {
        this.notification.showMessage(
          'error',
          result.error ?? 'outlook.emailNotification.failEntry',
          true,
        );
        return;
      }

      this.showNotification(type);
      this.markEntrySaved(key, type);
    } catch (error) {
      console.error('Error during saveEmail:', error);
      this.notification.showMessage(
        'error',
        'outlook.emailNotification.failEntry',
        true,
      );
    } finally {
      this.isLoading.spinner = false;
    }
  }

  async onSaveEmailDetail(key: string, type: EntryType): Promise<void> {
    this.saveEmailDetail = false;
    this.saveEmailDetailProperties = undefined;

    this.showNotification(type);
    this.markEntrySaved(key, type);

    await this.outlookService.saveOpportunityOrCaseInOutlook(key, type);
  }

  private markEntrySaved(key: string, type: EntryType): void {
    const entries =
      type === EntryType.Opportunity ? this.opportunities : this.cases;
    this.relatedService.updateSavedEmailStatus(entries, key);
  }

  private showNotification(type: EntryType) {
    if (type === EntryType.Opportunity) {
      const message = this.translate.instant(
        'outlook.emailNotification.successOpportunity',
        {
          oppLabel: this.translate.instant('outlook.entries.opportunity'),
        },
      );

      this.notification.showMessage('success', message);
    }

    if (type === EntryType.Case) {
      const message = this.translate.instant(
        'outlook.emailNotification.successCase',
        {
          caseLabel: this.verticalService.translateVertical('case'),
        },
      );

      this.notification.showMessage('success', message);
    }
  }
}
